import React, { createContext, useContext } from 'react';
import upperFirst from 'lodash/upperFirst';

import { NoProviderError } from '../react_context/errors';

export type ProviderComponentProps<TContextValue> = {
  value: TContextValue;
  children: React.ReactNode;
};

type ContextHelpers<TContextValue> = {
  useContextValue: () => TContextValue;
  /**
   * Optionally don't throw when not wrapped in the ContextProvider.
   *
   * This should be used sparingly, since it's easy to introduce unexpected
   * behavior here if the context value is not provided.
   */
  useConditionalContextValue: () => TContextValue | null;
  ContextProvider: (props: ProviderComponentProps<TContextValue>) => JSX.Element;
};

export function createContextAndProvider<TContextValue extends NonNullable<any>>(
  contextName: string,
): ContextHelpers<TContextValue> {
  const Context = createContext<TContextValue | null>(null);
  const capitalizedContextName = upperFirst(contextName);

  function useConditionalContextValue(): TContextValue | null {
    const contextValue = useContext(Context);
    return contextValue;
  }

  function useContextValue(): TContextValue {
    const contextValue = useConditionalContextValue();
    if (!contextValue) {
      throw new NoProviderError(capitalizedContextName);
    }
    return contextValue;
  }

  function ContextProvider(props: ProviderComponentProps<TContextValue>): JSX.Element {
    const { value, children } = props;
    return <Context.Provider value={value}>{children}</Context.Provider>;
  }

  return { useContextValue, useConditionalContextValue, ContextProvider };
}
